// apollo client
import { ApolloClient, InMemoryCache, gql } from '@apollo/client';
import fetch from 'unfetch';
import { ApolloLink, fromPromise } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { onError } from 'apollo-link-error';
import { GET_TOKEN, REFRESH_TOKEN } from './gql/auth';
import { history } from './App';
import { clearUser } from './actions/auth';

const cache = new InMemoryCache();

const getNewToken = (client) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await client.mutate({
        mutation: REFRESH_TOKEN,
        update: (cache, { data }) => {
          cache.writeQuery({
            query: GET_TOKEN,
            data: {
              token: data.refreshToken.token,
              netId: data.refreshToken.me.name,
              role: data.refreshToken.me.role,
              isLoggedIn: true,
              loading: false,
            },
          });
        },
      });

      resolve({ token: data.refreshToken.token });
    } catch (error) {
      reject(error);
    }
  });
};

// init cache
cache.writeQuery({
  query: gql`
    query initCache {
      token
      isLoggedIn
      netId
      role
      loading
      loginLoading
    }
  `,
  data: {
    token: '',
    isLoggedIn: false,
    netId: '',
    role: '',
    loading: true,
    loginLoading: true,
  },
});

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      // eslint-disable-next-line no-restricted-syntax
      for (const err of graphQLErrors) {
        const { message, locations, path, extensions } = err;
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, Extensions: ${JSON.stringify(
            extensions
          )}`
        );

        if (extensions.code === 'UNAUTHENTICATED') {
          console.warn(message);
          switch (message) {
            case 'LOGIN FAIL':
              break;
            case 'NO ME':
              // don't do anything
              // history.push('/redirectSSO');
              return;
            case 'GO LOGIN':
              if (client) {
                clearUser({ client });
              }
              history.push('/redirectSSO');
              break;
            case 'SESSION TIMEOUT':
              if (client) {
                clearUser({ client });
              }
              history.push('/redirectSSO');
              break;
            default:
              //refresh token

              // eslint-disable-next-line consistent-return
              return fromPromise(
                getNewToken(client)
                  .then(({ token }) => {
                    return token;
                  })
                  .catch((error) => {
                    if (client) {
                      clearUser({ client });
                    }
                    //history.push('/redirectSSO');
                    return;
                  })
              )
                .filter((value) => {
                  return Boolean(value);
                })
                .flatMap(() => {
                  // retry the request, returning the new observable
                  return forward(operation);
                });
          }
        }
      }
    }

    if (networkError) console.log(`[Network error]: ${networkError}`);
  }
);

const authLink = new ApolloLink((operation, forward) => {
  const { token } = cache.readQuery({ query: GET_TOKEN });

  if (token) {
    operation.setContext({
      headers: {
        'x-auth-token': token,
      },
    });
  }
  return forward(operation);
});

const httpLink = createHttpLink({
  uri: `${process.env.REACT_APP_SERVER_URI}/graphql`,
  credentials: 'include',
});

const client = new ApolloClient({
  cache,
  fetchOptions: { fetch },
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  resolvers: {},
});

export const getRefreshToken = async () => {
  try {
    await client.mutate({
      mutation: REFRESH_TOKEN,
      variables: { init: true },
      update: (cache, { data }) => {
        cache.writeQuery({
          query: GET_TOKEN,
          data: {
            token: data.refreshToken.token,
            netId: data.refreshToken.me.netId,
            role: data.refreshToken.me.role,
            loading: false,
            isLoggedIn: true,
          },
        });
      },
    });
  } catch (error) {
    client.writeQuery({ query: GET_TOKEN, data: { loading: false } });
    throw error;
  }
};

export default client;
